import React, { useContext, useMemo, useState } from 'react';
import { createApiClient } from 'api/apiClient';
import { DataGrid, GridColDef, GridSelectionModel } from '@mui/x-data-grid';
import { DiagnosisListResponse } from 'api/SharedTypes';
import {
  Autocomplete,
  Button,
  FilterOptionsState,
  FormControl,
  TextField,
} from '@mui/material';
import { useIntl } from 'react-intl';
import I18nKey from 'lib/I18nKeys';
import './ClinicalDiagnosis.css';
import isEmpty from 'lodash/isEmpty';
import { TabName } from 'components/UpdateDialog';
import { CircularProgress } from '@material-ui/core';
import { useMutation, useQuery } from 'react-query';
import { UserContext, UserContextState } from 'contexts/userContext';
import { UserRole } from 'services/Utils';

interface ClinicalDiagnosisProps {
  readonly patientInternalId: number;
  readonly tabName: TabName;
  readonly userRole: string;
}

interface DiagnosisData {
  readonly id: number;
  readonly abbreviated_desc: string;
  readonly assigned_code: string;
}

const columns: GridColDef[] = [
  {
    field: 'abbreviated_desc',
    headerName: 'Diagnoses',
    width: 300,
  },
  {
    field: 'assigned_code',
    headerName: '	ICD10',
  },
];

const EMPTY_SELECTED_DIAGNOSIS: DiagnosisListResponse = {
  category: '',
  code: '',
  abbreviated_desc: '',
};

const getDiagnosisOptionLabel = (
  option: DiagnosisListResponse | string,
): string => {
  if (typeof option === 'string') return option;
  if (!option || !option.abbreviated_desc) return '';
  return `${option.abbreviated_desc} ${option.category === '' ? '' : `(${option.category})`
    }`;
};

const filterAndSortDiagnoses = (
  options: DiagnosisListResponse[],
  state: FilterOptionsState<DiagnosisListResponse>,
) => {
  const query = state.inputValue.trim().toLowerCase();
  if (!query) {
    return options;
  }

  const filtered = options.filter(item => {
    const desc = (item.abbreviated_desc || '').toLowerCase();
    const cat = (item.category || '').toLowerCase();
    const code = (item.code || '').toLowerCase();
    return desc.includes(query) || cat.includes(query) || code.includes(query);
  });

  return filtered.sort((a, b) => {
    const aDesc = (a.abbreviated_desc || '').toLowerCase();
    const bDesc = (b.abbreviated_desc || '').toLowerCase();
    const aCat = (a.category || '').toLowerCase();
    const bCat = (b.category || '').toLowerCase();
    const aCode = (a.code || '').toLowerCase();
    const bCode = (b.code || '').toLowerCase();

    const aStartsWith =
      aDesc.startsWith(query) ||
      aCat.startsWith(query) ||
      aCode.startsWith(query);
    const bStartsWith =
      bDesc.startsWith(query) ||
      bCat.startsWith(query) ||
      bCode.startsWith(query);

    if (aStartsWith && !bStartsWith) return -1;
    if (!aStartsWith && bStartsWith) return 1;

    return aDesc.localeCompare(bDesc);
  });
};

export const ClinicalDiagnosis: React.FC<ClinicalDiagnosisProps> = ({
  patientInternalId,
  userRole,
  tabName,
}) => {
  const { isReadOnly } = useContext<UserContextState>(UserContext);
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedDiagnosis, setSelectedDiagnosis] =
    useState<DiagnosisListResponse>(EMPTY_SELECTED_DIAGNOSIS);
  const [selectedRows, setSelectedRows] = useState<DiagnosisData[]>([]);
  const [selectionModel, setSelectionModel] =
    React.useState<GridSelectionModel>([]);

  const accessToken = localStorage.getItem('access_token');
  const controller = new AbortController();
  const apiClient = createApiClient(accessToken!, controller);

  const intl = useIntl();

  const { userData } = useContext(UserContext);
  const OrgID = userData?.userOrg;
  const OrgId: string =
    OrgID !== undefined ? OrgID.toString() : 'default value';

  const { isLoading: isDiagnosisListLoading, data: diagnosisListResp } =
    useQuery('get-diagnosis-list', () => apiClient.getDiagnosisList(OrgId), {
      enabled: userRole === UserRole.patient,
    });

  const {
    isLoading: isPatientDiagnosisLoading,
    refetch: getPatientDiagnosis,
    data: diagnosisDataResp,
  } = useQuery(
    'get-patient-diagnosis',
    () => apiClient.getPatientDiagnoses(patientInternalId),
    {
      enabled: userRole === UserRole.patient,
    },
  );

  const { isLoading: isAddingDiagnosis, mutate: addDiagnosis } = useMutation(
    'add-patient-diagnosis',
    () => apiClient.addDiagnosis(patientInternalId, selectedDiagnosis.code),
    {
      onSuccess() {
        getPatientDiagnosis();
        setSelectedDiagnosis(EMPTY_SELECTED_DIAGNOSIS);
        setInputValue('');
      },
    },
  );

  const { isLoading: isDeletingDiagnosis, mutate: deleteDiagnosis } =
    useMutation(
      'add-patient-diagnosis',
      (diagnosisCode: string) =>
        apiClient.deleteDiagnosis(patientInternalId, diagnosisCode),
      {
        onSuccess() {
          getPatientDiagnosis();
          setSelectedRows([]);
          setSelectionModel([]);
        },
        onError: () => {
          setSelectedRows([]);
          setSelectionModel([]);
        },
      },
    );

  const diagnosisList = useMemo(() => {
    if (diagnosisListResp) {
      return diagnosisListResp;
    }
    return [];
  }, [diagnosisListResp]);

  const diagnosisData = useMemo(() => {
    if (diagnosisDataResp) {
      const diagnosis: DiagnosisData[] = diagnosisDataResp.map(
        (item, index) => ({
          id: index,
          ...item,
        }),
      );
      return diagnosis;
    }
    return [];
  }, [diagnosisDataResp]);

  const addPatientDiagnosis = () => addDiagnosis();

  const deletePatientDiagnosis = () => {
    const diagnosisCodes = selectedRows.map(item => item.assigned_code);
    deleteDiagnosis(diagnosisCodes.join());
  };

  const handleSelectedRowsChange = (newSelectionModel: GridSelectionModel) => {
    const selectedRows = diagnosisData.filter((_item, index) =>
      newSelectionModel.includes(index),
    );
    setSelectedRows(selectedRows);
    setSelectionModel(newSelectionModel);
  };

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="selectFlexContainer">
        <div>
          <FormControl className="diagnosisSelect">
            <Autocomplete
              id="diagnosis-list-select"
              disabled={isReadOnly}
              options={diagnosisList}
              loading={isDiagnosisListLoading}
              value={selectedDiagnosis.code ? selectedDiagnosis : null}
              onChange={(_event, newValue: DiagnosisListResponse | null) => {
                setSelectedDiagnosis(newValue || EMPTY_SELECTED_DIAGNOSIS);
              }}
              inputValue={inputValue}
              onInputChange={(_event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              getOptionLabel={getDiagnosisOptionLabel}
              isOptionEqualToValue={(option, val) => option.code === val.code}
              filterOptions={filterAndSortDiagnoses}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="standard"
                  label={intl.formatMessage({
                    id: I18nKey.PATIENT_DIAGNOSIS_SELECT_LABEL,
                  })}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {isDiagnosisListLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </FormControl>
        </div>
        <div className="selectFlexContainer">
          <div className="button">
            <Button
              variant="contained"
              disabled={
                isEmpty(selectedDiagnosis.code) ||
                isAddingDiagnosis ||
                isReadOnly
              }
              disableElevation
              onClick={addPatientDiagnosis}
            >
              {isAddingDiagnosis ? (
                <CircularProgress size={30} />
              ) : (
                intl.formatMessage({ id: I18nKey.PATIENT_DIAGNOSIS_ADD_LABEL })
              )}
            </Button>
          </div>
          <div className="button">
            <Button
              variant="contained"
              disabled={
                isEmpty(selectedRows) || isDeletingDiagnosis || isReadOnly
              }
              onClick={deletePatientDiagnosis}
              disableElevation
            >
              {isDeletingDiagnosis ? (
                <CircularProgress size={30} />
              ) : (
                intl.formatMessage({
                  id: I18nKey.PATIENT_DIAGNOSIS_REMOVE_LABEL,
                })
              )}
            </Button>
          </div>
        </div>
      </div>
      {isPatientDiagnosisLoading ? (
        <div className="spinnerContainer">
          <CircularProgress />
        </div>
      ) : (
        <div style={{ flexGrow: 1, display: 'flex' }}>
          <DataGrid
            rows={diagnosisData}
            columns={columns}
            checkboxSelection
            pageSize={5}
            autoHeight
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
            onSelectionModelChange={handleSelectedRowsChange}
            selectionModel={selectionModel}
          />
        </div>
      )}
    </div>
  );
};
